import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Clock, AlertCircle, Eye, Settings, ChevronRight, Lock, Zap, Star, Trophy, Coffee } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function NuevaSesion() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [sessionMode, setSessionMode] = useState(null); // null, 'blind', 'custom', 'advanced'
  const [horizon, setHorizon] = useState(24);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [dailyStats, setDailyStats] = useState(null);
  const [permissions, setPermissions] = useState({
    custom_sessions_enabled: false,
    advanced_sessions_unlocked: false,
    days_until_advanced: 40
  });

  useEffect(() => {
    fetchPermissions();
    fetchFavorites();
    fetchDailyStats();
  }, []);

  const fetchPermissions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/me/permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPermissions(data);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const fetchFavorites = async () => {
    setLoadingFavorites(true);
    try {
      const response = await fetch(`${API_URL}/api/users/me/favorites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorite_assets || []);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const fetchDailyStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/me/daily-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setDailyStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching daily stats:', error);
    }
  };

  const handleCreate = async () => {
    // Validate favorites for custom/advanced sessions
    if ((sessionMode === 'custom' || sessionMode === 'advanced') && favorites.length < 3) {
      toast.error('Necesitas configurar al menos 3 activos favoritos en tu Perfil');
      return;
    }

    setLoading(true);
    
    try {
      const body = {
        horizon_hours: sessionMode === 'blind' ? 12 : horizon,
        session_type: sessionMode
      };

      // Custom/Advanced: system picks from favorites (no symbol sent)
      // The backend will randomly select from user's favorites

      // Use XMLHttpRequest to avoid body stream issues
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/sessions`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onload = () => {
          try {
            const response = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(response);
            } else {
              reject(new Error(response.detail || 'Error al crear sesión'));
            }
          } catch (e) {
            reject(new Error('Error al procesar respuesta'));
          }
        };
        
        xhr.onerror = () => reject(new Error('Error de conexión'));
        xhr.send(JSON.stringify(body));
      });

      toast.success('Sesión creada');
      navigate(`/sesion/${data.id}`);
    } catch (error) {
      toast.error(error.message || 'Error al crear sesión');
    } finally {
      setLoading(false);
    }
  };

  // Mode selection screen
  if (!sessionMode) {
    const isAdmin = user?.role === 'admin';
    const customEnabled = permissions.custom_sessions_enabled || isAdmin;
    const advancedUnlocked = permissions.advanced_sessions_unlocked || isAdmin;
    const hasFavorites = favorites.length >= 3;

    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-2">Nueva Sesión ARV</h1>
        <p className="text-white/50 mb-6">Elige el tipo de sesión que deseas realizar</p>

        {/* Stop on a Win Warning */}
        {dailyStats?.recent_hit && (
          <div 
            className="mb-6 p-4 bg-[hsl(160,84%,39%)]/10 border border-[hsl(160,84%,39%)]/30 rounded-xl flex items-start gap-3"
            data-testid="stop-on-win-warning"
          >
            <Trophy className="w-5 h-5 text-[hsl(160,84%,39%)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Tu última sesión fue un acierto 🎯</p>
              <p className="text-white/60 text-xs mt-1">
                Considera "parar en victoria". La fatiga puede degradar tu rendimiento.
              </p>
            </div>
          </div>
        )}

        {/* Fatigue Warning */}
        {dailyStats?.fatigue_warning && (
          <div 
            className="mb-6 p-4 bg-[hsl(33,92%,55%)]/10 border border-[hsl(33,92%,55%)]/30 rounded-xl flex items-start gap-3"
            data-testid="fatigue-warning"
          >
            <Coffee className="w-5 h-5 text-[hsl(33,92%,55%)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Llevas {dailyStats.sessions_today} sesiones hoy</p>
              <p className="text-white/60 text-xs mt-1">
                Más de 2 sesiones diarias pueden reducir la precisión. Considera tomar un descanso.
              </p>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-4">
          {/* Blind Session */}
          <button
            onClick={() => setSessionMode('blind')}
            data-testid="mode-blind-button"
            className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-5 text-left hover:border-[hsl(198,62%,54%)] transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-[hsl(198,62%,54%)]/20 flex items-center justify-center mb-3 group-hover:bg-[hsl(198,62%,54%)]/30 transition-colors">
              <Eye className="w-5 h-5 text-[hsl(198,62%,54%)]" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-1">Sesión Ciega</h2>
            <p className="text-white/50 text-xs mb-3">
              Sistema elige activo. Todos predicen el mismo punto (12h).
            </p>
            <div className="flex items-center text-[hsl(198,62%,54%)] text-sm">
              <span>Comenzar</span>
              <ChevronRight className="w-4 h-4 ml-1" />
            </div>
          </button>

          {/* Custom Session */}
          <button
            onClick={() => {
              if (!customEnabled) {
                toast.error('Sesiones personalizadas no habilitadas. Contacta al admin.');
                return;
              }
              if (!hasFavorites) {
                toast.error('Configura 3-5 activos favoritos en tu Perfil primero');
                return;
              }
              setSessionMode('custom');
            }}
            data-testid="mode-custom-button"
            className={`bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-5 text-left transition-colors group ${
              customEnabled && hasFavorites
                ? 'hover:border-[hsl(252,37%,60%)]' 
                : 'opacity-60 cursor-not-allowed'
            }`}
          >
            <div className="w-10 h-10 rounded-lg bg-[hsl(252,37%,60%)]/20 flex items-center justify-center mb-3 relative">
              <Settings className="w-5 h-5 text-[hsl(252,37%,60%)]" />
              {(!customEnabled || !hasFavorites) && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <Lock className="w-2 h-2 text-white" />
                </div>
              )}
            </div>
            <h2 className="text-lg font-semibold text-white mb-1">Personalizada</h2>
            <p className="text-white/50 text-xs mb-3">
              {!customEnabled 
                ? 'Bloqueada por admin.'
                : !hasFavorites 
                  ? 'Requiere favoritos configurados.'
                  : 'Sistema elige de tus favoritos. No sabrás cuál hasta predecir.'}
            </p>
            <div className={`flex items-center text-sm ${customEnabled && hasFavorites ? 'text-[hsl(252,37%,60%)]' : 'text-white/30'}`}>
              <span>{customEnabled && hasFavorites ? 'Configurar' : !customEnabled ? 'Bloqueada' : 'Sin favoritos'}</span>
              {customEnabled && hasFavorites ? <ChevronRight className="w-4 h-4 ml-1" /> : <Lock className="w-4 h-4 ml-1" />}
            </div>
          </button>

          {/* Advanced Session */}
          <button
            onClick={() => {
              if (!advancedUnlocked) {
                toast.error(`Sesión avanzada se desbloquea en ${permissions.days_until_advanced} días.`);
                return;
              }
              if (!hasFavorites) {
                toast.error('Configura 3-5 activos favoritos en tu Perfil primero');
                return;
              }
              setSessionMode('advanced');
            }}
            data-testid="mode-advanced-button"
            className={`bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-5 text-left transition-colors group ${
              advancedUnlocked && hasFavorites
                ? 'hover:border-[hsl(45,93%,47%)]' 
                : 'opacity-60 cursor-not-allowed'
            }`}
          >
            <div className="w-10 h-10 rounded-lg bg-[hsl(45,93%,47%)]/20 flex items-center justify-center mb-3 relative">
              <Zap className="w-5 h-5 text-[hsl(45,93%,47%)]" />
              {(!advancedUnlocked || !hasFavorites) && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                  <Lock className="w-2 h-2 text-white" />
                </div>
              )}
            </div>
            <h2 className="text-lg font-semibold text-white mb-1">Avanzada</h2>
            <p className="text-white/50 text-xs mb-3">
              {!advancedUnlocked 
                ? `Desbloquea en ${permissions.days_until_advanced} días.`
                : !hasFavorites
                  ? 'Requiere favoritos configurados.'
                  : 'Solo 30 respiraciones. Máx 3/día.'}
            </p>
            <div className={`flex items-center text-sm ${advancedUnlocked && hasFavorites ? 'text-[hsl(45,93%,47%)]' : 'text-white/30'}`}>
              <span>{advancedUnlocked && hasFavorites ? 'Configurar' : !advancedUnlocked ? 'Bloqueada' : 'Sin favoritos'}</span>
              {advancedUnlocked && hasFavorites ? <ChevronRight className="w-4 h-4 ml-1" /> : <Lock className="w-4 h-4 ml-1" />}
            </div>
          </button>
        </div>

        {/* Favorites status banner */}
        <div className={`mt-6 rounded-xl p-4 ${hasFavorites ? 'bg-[hsl(160,84%,39%)]/10 border border-[hsl(160,84%,39%)]/30' : 'bg-[hsl(45,93%,47%)]/10 border border-[hsl(45,93%,47%)]/30'}`}>
          <div className="flex items-center gap-3">
            <Star className={`w-5 h-5 ${hasFavorites ? 'text-[hsl(160,84%,39%)]' : 'text-[hsl(45,93%,47%)]'}`} />
            <div className="flex-1">
              {hasFavorites ? (
                <p className="text-white text-sm">
                  Tienes <span className="font-medium">{favorites.length} activos favoritos</span> configurados: {favorites.slice(0, 3).join(', ')}{favorites.length > 3 ? '...' : ''}
                </p>
              ) : (
                <p className="text-white text-sm">
                  <span className="font-medium">Configura tus favoritos</span> para usar sesiones Personalizada y Avanzada
                </p>
              )}
            </div>
            <Link 
              to="/perfil" 
              className={`text-sm font-medium ${hasFavorites ? 'text-[hsl(160,84%,39%)]' : 'text-[hsl(45,93%,47%)]'} hover:underline`}
            >
              {hasFavorites ? 'Editar' : 'Configurar'} →
            </Link>
          </div>
        </div>

        {/* Info about limits */}
        <div className="mt-4 bg-white/5 rounded-xl p-4 text-center">
          <p className="text-white/60 text-sm">
            Puedes realizar <span className="text-white font-medium">1 ciega</span>, <span className="text-white font-medium">1 personalizada</span> y <span className="text-white font-medium">hasta 3 avanzadas</span> por día
          </p>
        </div>
      </div>
    );
  }

  // Get session color based on type
  const getSessionColor = () => {
    if (sessionMode === 'blind') return 'hsl(198,62%,54%)';
    if (sessionMode === 'advanced') return 'hsl(45,93%,47%)';
    return 'hsl(252,37%,60%)';
  };

  const sessionTitles = {
    blind: 'Sesión Ciega',
    custom: 'Sesión Personalizada',
    advanced: 'Sesión Avanzada'
  };

  const sessionDescriptions = {
    blind: 'Todos predicen el mismo activo del día',
    custom: 'Sistema elige de tus favoritos sin revelar cuál',
    advanced: 'Sesión rápida con 30 respiraciones'
  };

  // Configuration screen
  return (
    <div className="p-8 max-w-2xl mx-auto">
      <button
        onClick={() => setSessionMode(null)}
        className="text-white/50 hover:text-white text-sm mb-4 flex items-center gap-1"
      >
        ← Cambiar tipo de sesión
      </button>

      <h1 className="text-3xl font-bold text-white mb-2">
        {sessionTitles[sessionMode]}
      </h1>
      <p className="text-white/50 mb-8">
        {sessionDescriptions[sessionMode]}
      </p>

      {/* Info Card */}
      <div 
        className="border rounded-xl p-6 mb-8"
        style={{ 
          backgroundColor: `${getSessionColor()}10`,
          borderColor: `${getSessionColor()}30`
        }}
      >
        <div className="flex gap-4">
          <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: getSessionColor() }} />
          <div>
            <h3 className="font-medium text-white mb-2">Protocolo ARV</h3>
            <ul className="text-sm text-white/70 space-y-1">
              {sessionMode === 'blind' ? (
                <>
                  <li>• No sabrás qué activo se está prediciendo hasta enviar</li>
                  <li>• Comenzarás con 40 respiraciones de canalización</li>
                  <li>• Recibirás instrucciones de enfoque antes de visualizar</li>
                  <li>• Las imágenes aparecerán después de registrar tus impresiones</li>
                </>
              ) : sessionMode === 'advanced' ? (
                <>
                  <li>• <strong className="text-white">No sabrás qué activo es</strong> (seleccionado de tus favoritos)</li>
                  <li>• Solo 30 respiraciones antes de comenzar</li>
                  <li>• Pasarás directamente a ver las imágenes</li>
                  <li>• El resultado se verificará en {horizon} horas</li>
                </>
              ) : (
                <>
                  <li>• El sistema elegirá aleatoriamente uno de tus favoritos</li>
                  <li>• <strong className="text-white">No sabrás qué activo es</strong> hasta después de predecir</li>
                  <li>• Comenzarás con 40 respiraciones de canalización</li>
                  <li>• El resultado se verificará en {horizon} horas</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Favorites info for custom/advanced */}
      {(sessionMode === 'custom' || sessionMode === 'advanced') && (
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Star className="w-5 h-5 text-[hsl(45,93%,47%)]" />
            <h2 className="text-lg font-medium text-white">Tu Cesta de Favoritos</h2>
          </div>
          <p className="text-white/60 text-sm mb-4">
            El sistema elegirá aleatoriamente uno de tus activos configurados. 
            <strong className="text-white"> No sabrás cuál</strong> hasta después de enviar tu predicción.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {favorites.map(symbol => (
              <span 
                key={symbol}
                className="px-3 py-1.5 rounded-lg text-sm bg-white/5 text-white/70"
              >
                {symbol}
              </span>
            ))}
          </div>
          <Link 
            to="/perfil" 
            className="text-[hsl(198,62%,54%)] hover:underline text-sm"
          >
            → Editar favoritos en mi perfil
          </Link>
        </div>
      )}

      {/* Horizon Selection */}
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Clock className="w-5 h-5 text-white/70" />
          <h2 className="text-lg font-medium text-white">Horizonte de predicción</h2>
        </div>

        {sessionMode === 'blind' ? (
          /* Sesión Ciega: Solo 12 horas, sin opción */
          <div className="p-4 rounded-xl border-2 border-[hsl(198,62%,54%)] bg-[hsl(198,62%,54%)]/10 text-center">
            <p className="text-2xl font-bold text-white">12h</p>
            <p className="text-sm text-white/50">Todos predicen el mismo punto del día</p>
          </div>
        ) : (
          /* Sesión Personalizada/Avanzada: Opciones de tiempo */
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 4, 24, 48].map((h) => (
              <button
                key={h}
                onClick={() => setHorizon(h)}
                data-testid={`horizon-${h}h-button`}
                className={`p-4 rounded-xl border-2 transition-all ${
                  horizon === h
                    ? sessionMode === 'advanced'
                      ? 'border-[hsl(45,93%,47%)] bg-[hsl(45,93%,47%)]/10'
                      : 'border-[hsl(252,37%,60%)] bg-[hsl(252,37%,60%)]/10'
                    : 'border-[hsl(232,18%,18%)] hover:border-white/20'
                }`}
              >
                <p className="text-2xl font-bold text-white">{h}h</p>
                <p className="text-sm text-white/50">{h === 1 ? '1 hora' : h === 4 ? '4 horas' : h === 24 ? '1 día' : '2 días'}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Summary for custom/advanced */}
      {(sessionMode === 'custom' || sessionMode === 'advanced') && (
        <div className="bg-white/5 rounded-xl p-4 mb-6">
          <p className="text-white/70 text-sm">
            El sistema elegirá aleatoriamente uno de tus <span className="text-white font-medium">{favorites.length} favoritos</span>.{' '}
            Predecirás si subirá o bajará en las próximas{' '}
            <span className="text-white font-medium">{horizon} horas</span>
            {sessionMode === 'advanced' && <span className="text-[hsl(45,93%,47%)]"> (30 respiraciones)</span>}
          </p>
        </div>
      )}

      {/* Create Button */}
      <button
        onClick={handleCreate}
        disabled={loading || ((sessionMode === 'custom' || sessionMode === 'advanced') && favorites.length < 3)}
        data-testid="crear-sesion-button"
        className={`w-full ${
          sessionMode === 'blind'
            ? 'bg-[hsl(198,62%,54%)] hover:bg-[hsl(198,62%,48%)]'
            : sessionMode === 'advanced'
              ? 'bg-[hsl(45,93%,47%)] hover:bg-[hsl(45,93%,42%)]'
              : 'bg-[hsl(252,37%,60%)] hover:bg-[hsl(252,37%,55%)]'
        } disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-4 rounded-xl transition-colors`}
      >
        {loading ? 'Creando sesión...' : 'Comenzar sesión'}
      </button>
    </div>
  );
}
